import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { EndPageMessage, EndState } from '../../../common/interfaces';
import { END_STATE, EndStateContext, resolveEndState } from '../../../common/lib/endPage';
import { ScreeningService } from './screening.service';
import { SharedService } from './shared.service';

/**
 * Single entry point for sending a respondent who cannot proceed to the end page.
 * The state comes from resolveEndState and the copy from the screening's configured messages.
 */
@Injectable({
  providedIn: 'root',
})
class EndPageService {

  constructor(
    private router: Router,
    private screeningService: ScreeningService,
    private sharedService: SharedService
  ) { }

  public resolve(screening: any, context: EndStateContext = {}): EndState {
    return resolveEndState({
      screeningStatus: screening && screening.status,
      confirmedCount: screening && screening.confirmedCount,
      rsvpMax: screening && screening.rsvpMax,
      ...context
    });
  }

  /**
   * Fetches the message for the resolved state. A failed lookup yields an empty message rather
   * than an error, so the respondent still lands on the end page.
   */
  public getMessage(screening: any, context: EndStateContext = {}): Observable<EndPageMessage> {
    const endState = this.resolve(screening, context);
    if (!screening || screening.id == null) {
      return of({ endState, header: '', body: '' } as EndPageMessage);
    }
    return this.screeningService.getEndPageMessage(screening.id, endState).pipe(
      catchError(() => of({ endState, header: '', body: '' } as EndPageMessage))
    );
  }

  // registrationId is only used by the Waitlisted page, which lets the respondent remove themselves
  public show(screening: any, context: EndStateContext = {}, registrationId?: number): void {
    this.getMessage(screening, context).subscribe((message) => {
      this.sharedService.setRegistrationError({
        errorTitle: message.header,
        pageTitle: message.header,
        message: message.body,
        endState: message.endState,
        registrationId: message.endState === END_STATE.WAITLISTED ? registrationId : undefined
      });
      this.router.navigate(['registration-error']);
    });
  }

  /**
   * Same as show, but reloads the screening first so closed/full is judged on current data.
   */
  public showFresh(screeningId: number, context: EndStateContext = {}): void {
    this.screeningService.getById(screeningId).pipe(
      catchError(() => of({ id: screeningId }))
    ).subscribe((screening) => this.show(screening, context));
  }
}

export { EndPageService };
